import React from "react";

/**
 * Iter209bl — Avatar utente unificato per Housetag.
 *
 * Mostra la foto profilo se presente (`avatar_signed_url` | `avatar_url`),
 * altrimenti un cerchio colorato con le iniziali (nome + cognome). Il
 * colore di sfondo è deterministico rispetto al nome, così ogni utente
 * ha un colore consistente in ogni pagina. Nessun rendering "buco".
 *
 * Uso:
 *   <UserAvatar user={u} size={40} />
 *   <UserAvatar user={u} size={64} rounded={false} />
 *
 * Il componente accetta anche un `role` per applicare il gradient viola
 * ai pro se lo si desidera (opzionale). Default: hash color.
 */

const PALETTE = [
  ["from-purple-500", "to-fuchsia-500"],
  ["from-blue-500", "to-cyan-500"],
  ["from-emerald-500", "to-teal-500"],
  ["from-amber-500", "to-orange-500"],
  ["from-rose-500", "to-pink-500"],
  ["from-indigo-500", "to-violet-500"],
  ["from-sky-500", "to-blue-500"],
  ["from-lime-500", "to-emerald-500"],
];

function hashIdx(str) {
  let h = 0;
  const s = (str || "").toString();
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % PALETTE.length;
}

function computeInitials(user) {
  const first = (user?.first_name || "").trim();
  const last = (user?.last_name || "").trim();
  if (first || last) {
    return ((first[0] || "") + (last[0] || "")).toUpperCase();
  }
  const company = (user?.company_name || user?.company_insegna || "").trim();
  if (company) {
    const parts = company.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
  }
  const email = (user?.email || "").trim();
  return (email[0] || "?").toUpperCase();
}

function getAvatarSrc(user) {
  const raw = user?.avatar_signed_url || user?.avatar_url;
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${raw}`;
}

export default function UserAvatar({
  user,
  size = 40,
  className = "",
  rounded = true,
  useRoleGradient = false,
  testId,
}) {
  const src = getAvatarSrc(user);
  const initials = computeInitials(user);
  const idx = hashIdx(user?.id || user?._id || user?.email || initials);
  const [from, to] = PALETTE[idx];
  const grad = useRoleGradient ? "ht-gradient" : `bg-gradient-to-br ${from} ${to}`;
  const fontSize = Math.max(10, Math.round(size * 0.38));
  const dimensions = { width: `${size}px`, height: `${size}px`, fontSize: `${fontSize}px` };
  const shape = rounded ? "rounded-full" : "rounded-md";
  const testProps = testId ? { "data-testid": testId } : {};

  if (src) {
    return (
      <img
        src={src}
        alt={initials}
        style={dimensions}
        className={`${shape} object-cover shrink-0 ${className}`}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        {...testProps}
      />
    );
  }
  return (
    <div
      style={dimensions}
      className={`${shape} shrink-0 flex items-center justify-center font-semibold text-white select-none ${grad} ${className}`}
      {...testProps}
    >
      {initials}
    </div>
  );
}
